"use client";

import Image from "next/image";
import { useState } from "react";
import { ArrowRight } from "@phosphor-icons/react";
import { useTranslations } from "next-intl";

import { Link } from "@/navigation";

interface QualityImage {
	src: string;
	altKey: string;
}

interface QualityRow {
	col1Key: string;
	col2Key: string;
}

interface IndustryQualityComplianceProps {
	namespace: string;
	images: readonly QualityImage[];
	rows: readonly QualityRow[];
	features: readonly string[];
	brandColor?: string;
}

export default function IndustryQualityCompliance({
	namespace,
	images,
	rows,
	features,
	brandColor = "#64a525",
}: IndustryQualityComplianceProps) {
	const t = useTranslations(namespace);
	const [activeIndex, setActiveIndex] = useState(0);
	const activeImage = images[activeIndex] ?? images[0];

	return (
		<section className="bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:gap-12">
					<div className="flex flex-col items-center lg:w-[42%]">
						<div className="relative w-full max-w-sm overflow-hidden shadow-lg lg:max-w-none">
							<div className="relative aspect-[280/350] w-full">
								<Image
									src={activeImage.src}
									alt={t(activeImage.altKey)}
									fill
									className="object-cover transition-opacity duration-300"
									sizes="(max-width: 1024px) 80vw, 42vw"
								/>
							</div>
						</div>
						<div className="mt-4 flex items-center gap-2">
							{images.map((_, i) => (
								<button
									key={i}
									onClick={() => setActiveIndex(i)}
									aria-label={t("qualityImageDotLabel", { index: i + 1 })}
									className="h-2.5 w-2.5 rounded-full transition-all duration-200 hover:opacity-80"
									style={{ backgroundColor: i === activeIndex ? brandColor : "#c5c5c5" }}
								/>
							))}
						</div>
					</div>

					<div className="flex flex-1 flex-col">
						<h2 className="mb-3 text-2xl font-bold leading-[1.3] text-black sm:mb-4 sm:text-[30px] sm:leading-[1.28] md:text-4xl md:leading-[1.25]">
							{t("qualityTitle")}
						</h2>
						<p className="mb-6 text-sm leading-relaxed text-black sm:mb-8 md:text-base">{t("qualityDesc")}</p>

						<div className="border-t border-[#cfcfcf]">
							{rows.map((row) => (
								<div key={row.col1Key} className="grid grid-cols-1 border-b border-[#cfcfcf] sm:grid-cols-2">
									<div className="px-4 py-3 text-sm leading-snug text-black md:px-5 md:py-4 md:text-base">
										{t(row.col1Key)}
									</div>
									<div className="border-t border-[#cfcfcf] px-4 py-3 text-sm leading-snug text-black sm:border-t-0 md:px-5 md:py-4 md:text-base">
										{t(row.col2Key)}
									</div>
								</div>
							))}
						</div>

						<p className="mt-4 border-b border-[#cfcfcf] pb-4 text-sm leading-relaxed text-black md:text-base">
							{t("qualityCertNote")}
						</p>
					</div>
				</div>

				<div className="mt-8 grid grid-cols-1 gap-4 sm:mt-10 sm:grid-cols-2 lg:mt-12 lg:grid-cols-4 lg:gap-5">
					{features.map((feature) => (
						<div
							key={feature}
							className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-[#ebf5ea] px-5 py-5 transition-shadow duration-300 hover:shadow-md"
						>
							<p className="text-base font-bold text-black">{t(feature)}</p>
							<Link
								href="/quality-assurance"
								className="inline-flex items-center gap-1 text-sm font-semibold transition-colors duration-200 hover:opacity-75"
								style={{ color: brandColor }}
							>
								{t("qualityFeatureLearnMore")}
								<ArrowRight size={15} weight="bold" />
							</Link>
						</div>
					))}
				</div>
			</div>
		</section>
	);
}
